import { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import "../../styles/theme.css";

// Mirrors THEME_COOKIE in utils/siteVariant.server.js, which the loaders read
// so the server renders the visitor's choice. localStorage is kept only to
// migrate visitors who chose a theme before the cookie existed.
const COOKIE_KEY = "pendora-theme";
const STORAGE_KEY = "pendora-theme";
const ONE_YEAR = 60 * 60 * 24 * 365;

export const THEME_CANVAS = { cream: "#f3ede2", night: "#07061c" };

const isTheme = (v) => v === "cream" || v === "night";

function readSaved() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(v) ? v : null;
  } catch {
    return null;
  }
}

function readCookie() {
  try {
    const match = document.cookie
      .split(";")
      .map((p) => p.trim())
      .find((p) => p.startsWith(`${COOKIE_KEY}=`));
    const v = match ? decodeURIComponent(match.slice(COOKIE_KEY.length + 1)) : null;
    return isTheme(v) ? v : null;
  } catch {
    return null;
  }
}

function persist(theme) {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${COOKIE_KEY}=${theme}; Path=/; Max-Age=${ONE_YEAR}; SameSite=Lax${secure}`;
  } catch {
    /* cookies unavailable: the switch still works for this page */
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* storage unavailable */
  }
}

/**
 * The site's light/dark theme. `initial` is what the server rendered: the
 * visitor's cookie when they have one, else the port's default, so the first
 * paint is already right. toggle(event) switches with a circular reveal
 * spreading from the click, using the View Transitions API where available.
 */
export function useSiteTheme(initial = "cream") {
  const [theme, setTheme] = useState(initial);

  // One-time migration: a choice saved in localStorage before the cookie
  // existed. Once the cookie is set the server renders it and this is a no-op.
  useEffect(() => {
    if (readCookie()) return;
    const saved = readSaved();
    if (!saved) return;
    persist(saved);
    if (saved !== initial) setTheme(saved);
  }, [initial]);

  // Keep the canvas behind the page in step, so overscroll never flashes.
  useEffect(() => {
    const html = document.documentElement;
    html.style.backgroundColor = THEME_CANVAS[theme];
    html.style.colorScheme = theme === "night" ? "dark" : "light";
  }, [theme]);

  const toggle = useCallback(
    (event) => {
      const next = theme === "night" ? "cream" : "night";
      persist(next);
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (!document.startViewTransition || reduced) {
        setTheme(next);
        return;
      }
      // Start the circle at the switch itself. Percentages of the viewport
      // keep this right on every screen density (pixel values were read at
      // half scale on Retina displays).
      const btn = event?.currentTarget?.getBoundingClientRect?.();
      const cx = btn ? btn.left + btn.width / 2 : event?.clientX ?? window.innerWidth - 60;
      const cy = btn ? btn.top + btn.height / 2 : event?.clientY ?? 40;
      const px = ((cx / window.innerWidth) * 100).toFixed(2);
      const py = ((cy / window.innerHeight) * 100).toFixed(2);
      const html = document.documentElement;
      html.classList.add("pc-theme-switching");
      const transition = document.startViewTransition(() => {
        flushSync(() => setTheme(next));
      });
      transition.ready
        .then(() => {
          html.animate(
            // 150% of the reference radius always reaches the far corner.
            { clipPath: [`circle(0% at ${px}% ${py}%)`, `circle(150% at ${px}% ${py}%)`] },
            { duration: 750, easing: "cubic-bezier(0.22, 1, 0.36, 1)", pseudoElement: "::view-transition-new(root)" }
          );
        })
        .catch(() => {});
      transition.finished.finally(() => html.classList.remove("pc-theme-switching"));
    },
    [theme]
  );

  return [theme, toggle];
}

/** Sun and moon button that flips the site theme. */
export function ThemeToggle({ theme, onToggle, className = "" }) {
  const night = theme === "night";
  return (
    <button
      type="button"
      className={`pc-theme ${className}`}
      onClick={onToggle}
      aria-label={night ? "Switch to light theme" : "Switch to dark theme"}
      title={night ? "Light theme" : "Dark theme"}
    >
      <ThemeIcon />
    </button>
  );
}

/** The sun/moon glyph on its own; CSS shows whichever fits the theme. */
export function ThemeIcon() {
  return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
        <g className="pc-theme__sun">
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" />
        </g>
        <path className="pc-theme__moon" d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />
      </svg>
  );
}
